(function() {
  try {
    const originalFetch = window.fetch;
    const urlParams = new URLSearchParams(window.location.search);
    
    // Debug configuration - can be enabled via URL parameter or manual override
    const DEBUG = urlParams.get('ccl-debug') === 'true' || false;

    // Create logger function
    const createLogger = () => {
      const colors = {
        info: "#3498db",
        success: "#2ecc71",
        error: "#e74c3c",
        warning: "#f39c12"
      };

      const logFn = (type, ...args) => {
        if (DEBUG) {
          console.log(
            `%cCCL`,
            `background: ${colors[type]}; color: white; padding: 2px 5px; border-radius: 3px;`,
            ...args
          );
        }
      };

      return {
        info: (...args) => logFn("info", ...args),
        success: (...args) => logFn("success", ...args),
        error: (...args) => logFn("error", ...args),
        warning: (...args) => logFn("warning", ...args)
      };
    };

    const log = createLogger();

    // Configuration object for different discount codes and their actions
    const DISCOUNT_ACTIONS = {
      'BNDLFREEGIFT': {
        pattern: /^BNDLFREEGIFT$/,
        variantIds: ['43208521973804', '42756005756972']
      },
      'BNDLGIFT': {
        pattern: /^BNDLGIFT$/,
        variantIds: ['43208521973804', '42756005756972']
      },
      // 'SLEEPY': {
      //   pattern: /^SLEEPY$/,
      //   variantIds: ['40284214591532']
      // },
      'DAD15GIFT': {
        pattern: /^DAD15GIFT$/,
        variantIds: ['43942367920172']
      }
    };

    const ICART_API_URL = 'https://icartapi.identixweb.com/api';

    log.info('Custom Cart Logic initialized', {
      debug: DEBUG,
      discountActions: Object.keys(DISCOUNT_ACTIONS)
    });

    /**
     * Core logic: checks for discount patterns and adds the corresponding products if needed.
     * Returns a promise that resolves when the operation is complete.
     */
    async function handleDiscountCheck(requestBody) {
      if (requestBody && requestBody.discount_code) {
        // Loop through all discount actions to find a match
        for (const [actionName, config] of Object.entries(DISCOUNT_ACTIONS)) {
          if (config.pattern.test(requestBody.discount_code)) {
            log.info(`Matched discount code "${requestBody.discount_code}" with pattern ${actionName}`);
            try {
              log.info('Checking if required products are already in cart...');
              const cart = await originalFetch('/cart.js').then(res => res.json());
              
              // Check which variant IDs are missing from the cart
              const variantsToAdd = config.variantIds.filter(variantId => {
                const isInCart = cart.items.some(item => item.variant_id.toString() === variantId);
                return !isInCart;
              });

              if (variantsToAdd.length === 0) {
                log.info('All required products are already in cart. Proceeding with original request.');
              } else {
                log.info(`Adding ${variantsToAdd.length} missing product(s) to cart. Pausing original request.`, variantsToAdd);
                
                // Add each missing variant to the cart
                for (const variantId of variantsToAdd) {
                  await addItemToCart(variantId, 1);
                }
                
                log.success('All required products added. Resuming original request.');
              }
              
              // Break after first match (assuming discount codes are mutually exclusive)
              break;
            } catch (error) {
              log.error(`Error during discount check and add to cart for ${actionName}:`, error);
            }
          }
        }
        
        // If no pattern matched
        log.info('No matching discount code pattern found in request body.');
      }
    }

    /**
     * Adds an item to the cart using the original fetch with a JSON payload.
     */
    function addItemToCart(variantId, quantity) {
      log.info(`addItemToCart called with variantId: ${variantId}, quantity: ${quantity}`);
      const data = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      return originalFetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(data)
        })
        .then(async (response) => {
          if (!response.ok) {
            log.error('Failed to add item to cart. Response status:', response.status);
            const errorData = await response.json().catch(() => ({
              description: 'Could not parse error response as JSON.'
            }));
            log.error('Error response from /cart/add.js:', errorData);
            throw new Error('Failed to add item to cart');
          }
          return response.json();
        })
        .then(addedItems => {
          log.success('Successfully added product to cart. Server response:', addedItems);
          if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            log.info('Publishing cartUpdate event.');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'custom-cart-logic-interceptor'
            });
          }
          return addedItems;
        })
        .catch(error => {
          log.error('Error in addItemToCart:', error);
          throw error;
        });
    }

    // --- XMLHttpRequest (XHR) Interception ---
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._method = method;
      this._url = url;
      return originalXhrOpen.apply(this, [method, url, ...args]);
    };

    XMLHttpRequest.prototype.send = function(body) {
      const xhr = this;
      if (this._url && this._url.startsWith(ICART_API_URL) && this._method.toUpperCase() === 'POST' && body) {
        log.info('Intercepted XHR request to iCart API.');
        try {
          const requestBody = JSON.parse(body);
          log.info('XHR iCart request body:', requestBody);

          // Pause the original send, run our logic, then resume.
          handleDiscountCheck(requestBody)
            .catch(err => log.error('Error in XHR handleDiscountCheck, request will still be sent.', err))
            .finally(() => {
              log.info('Proceeding with original XHR send call.');
              originalXhrSend.apply(xhr, [body]);
            });
          return; // IMPORTANT: We return here because we are handling the send asynchronously.
        } catch (error) {
          log.warning('Error processing XHR body. The request body might not be JSON. Letting it proceed.', error);
        }
      }

      // For all other requests, proceed as normal.
      return originalXhrSend.apply(this, [body]);
    };
  } catch (error) {
    console.error('CCL: A critical error occurred in the custom cart logic script. The script has been safely disabled. Error:', error);
  }
})(); 